import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import api from '@/lib/api';
import { humanizeEnum } from '@/lib/utils';
import { RecordLink } from './RecordLink';
import { Money, Row, SourceRow, day, type RecordSource } from './bodyParts';

export interface InvoiceDetail {
  id: string;
  invoiceNumber: string;
  status: string;
  createdAt: string;
  subtotal: number | string;
  discountAmount: number | string;
  lineDiscountAmount?: number | string;
  promotionDiscountAmount?: number | string;
  loyaltyDiscountAmount?: number | string;
  furtherTaxAmount?: number | string;
  tipAmount?: number | string;
  changeAmount?: number | string;
  taxAmount: number | string;
  totalAmount: number | string;
  paidAmount: number | string;
  customer?: { name?: string | null } | null;
  items: Array<{ id?: string; productName?: string; quantity?: number; unitPrice?: number | string; totalAmount?: number | string }>;
  payments: Array<{ id?: string; method?: string; amount?: number | string }>;
  fbrSubmitted?: boolean;
  fbrInvoiceId?: string | null;
  journalEntry?: RecordSource | null;
}

interface ReturnRow { id: string; returnNumber: string; totalRefund: number | string }

/** Read-only invoice (a POS sale) for the record viewer (POS-I238). */
export function InvoiceDetailBody({ data }: { data: InvoiceDetail }) {
  const { t } = useTranslation('records');
  const returns = useQuery({
    queryKey: ['record-viewer', 'invoice-returns', data.id],
    queryFn: async () => (await api.get<ReturnRow[]>(`/sales/${data.id}/returns`)).data,
    retry: false,
  });
  return (
    <div className="space-y-5 text-sm">
      <dl className="space-y-1.5">
        <Row label={t('rvDate')}><span dir="ltr">{day(data.createdAt)}</span></Row>
        {data.customer?.name && <Row label={t('rvCustomer')}>{data.customer.name}</Row>}
        {data.fbrSubmitted !== undefined && (
          <Row label={t('rvFbr')}>{data.fbrSubmitted ? (data.fbrInvoiceId ? t('rvFbrSubmittedRef', { ref: data.fbrInvoiceId }) : t('rvFbrSubmitted')) : t('rvFbrNotSubmitted')}</Row>
        )}
      </dl>
      {data.items.length === 0 ? (
        <p className="text-muted-foreground">{t('rvNoItems')}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b text-xs text-muted-foreground">
                <th className="py-1 text-start font-medium">{t('rvProduct')}</th>
                <th className="py-1 text-end font-medium">{t('rvQty')}</th>
                <th className="py-1 text-end font-medium">{t('rvLineTotal')}</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((i, n) => (
                <tr key={i.id ?? n} className="border-b align-top last:border-0">
                  <td className="py-1.5 break-words">{i.productName}</td>
                  <td className="py-1.5 text-end tabular-nums" dir="ltr">{i.quantity}</td>
                  <td className="py-1.5 text-end"><Money value={i.totalAmount} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <dl className="space-y-1.5 border-t pt-3">
        <Row label={t('rvSubtotal')}><Money value={data.subtotal} /></Row>
        {Number(data.lineDiscountAmount) > 0 && <Row label={t('rvLineDiscount')}><Money value={data.lineDiscountAmount} /></Row>}
        {Number(data.discountAmount) > 0 && <Row label={t('rvDiscount')}><Money value={data.discountAmount} /></Row>}
        {Number(data.promotionDiscountAmount) > 0 && <Row label={t('rvPromotionDiscount')}><Money value={data.promotionDiscountAmount} /></Row>}
        {Number(data.loyaltyDiscountAmount) > 0 && <Row label={t('rvLoyaltyDiscount')}><Money value={data.loyaltyDiscountAmount} /></Row>}
        <Row label={t('rvTax')}><Money value={data.taxAmount} /></Row>
        {Number(data.furtherTaxAmount) > 0 && <Row label={t('rvFurtherTax')}><Money value={data.furtherTaxAmount} /></Row>}
        {Number(data.tipAmount) > 0 && <Row label={t('rvTip')}><Money value={data.tipAmount} /></Row>}
        <div className="font-semibold"><Row label={t('rvGrandTotal')}><Money value={data.totalAmount} /></Row></div>
      </dl>
      {data.payments.length > 0 && (
        <dl className="space-y-1.5">
          <dt className="text-xs font-medium text-muted-foreground">{t('rvPayments')}</dt>
          {data.payments.map((p, n) => (
            <Row key={p.id ?? n} label={humanizeEnum(p.method)}><Money value={p.amount} /></Row>
          ))}
          {Number(data.changeAmount) > 0 && <Row label={t('rvChange')}><Money value={data.changeAmount} /></Row>}
        </dl>
      )}
      {returns.data && returns.data.length > 0 && (
        <dl className="space-y-1.5" data-testid="record-viewer-source">
          <dt className="text-xs font-medium text-muted-foreground">{t('rvReturns')}</dt>
          {returns.data.map((r) => (
            <Row key={r.id} label={t('rvRefund')}>
              <RecordLink type="saleReturn" id={r.id} label={r.returnNumber} />
              <span className="ms-2"><Money value={r.totalRefund} /></span>
            </Row>
          ))}
        </dl>
      )}
      <SourceRow items={[{ label: t('rvJournalEntry'), source: data.journalEntry }]} />
    </div>
  );
}
